
import { useToast } from '@/hooks/use-toast';
import { useCallback } from 'react';
import { logger } from '@/utils/logger';
import { UnknownObject } from '@/shared/types/strict';

export type ErrorContext = {
  operation?: string;
  component?: string;
  userId?: string;
  metadata?: UnknownObject;
}

export type ErrorLog = {
  timestamp: string;
  error: Error;
  context: ErrorContext;
  userAgent: string;
  url: string;
}

export type ApiError = {
  status: number;
  statusText: string;
  data?: UnknownObject;
}

export type ValidationError = {
  field: string;
  message: string;
}

export type PerformanceData = {
  operation: string;
  duration: number;
  metadata?: UnknownObject;
}

export type UserActionData = {
  action: string;
  element?: string;
  page?: string;
  data?: UnknownObject;
}

export type ApiRequestData = {
  method: string;
  url: string;
  data?: UnknownObject;
  headers?: UnknownObject;
}

export type ApiResponseData = {
  status: number;
  url: string;
  data?: UnknownObject;
  duration?: number;
}

export class CentralizedErrorHandler {
  private static instance: CentralizedErrorHandler;
  private errorLogs: ErrorLog[] = [];
  private maxLogs = 100;
  public logger = logger;

  static getInstance(): CentralizedErrorHandler {
    if (!CentralizedErrorHandler.instance) {
      CentralizedErrorHandler.instance = new CentralizedErrorHandler();
    }
    return CentralizedErrorHandler.instance;
  }

  /**
   * Handle generic errors with optional context
   */
  handleError(error: Error, context?: ErrorContext): boolean {
    try {
      const errorLog: ErrorLog = {
        timestamp: new Date().toISOString(),
        error,
        context: context || {},
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      this.errorLogs.unshift(errorLog);
      
      if (this.errorLogs.length > this.maxLogs) {
        this.errorLogs = this.errorLogs.slice(0, this.maxLogs);
      }

      const errorData = {
        message: error.message,
        stack: error.stack,
        name: error.name,
        context,
        timestamp: Date.now(),
        environment: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        }
      };

      this.logger.error('Error occurred', errorData);
      return false;
    } catch (handlingError) {
      // eslint-disable-next-line no-console
      console.error('Error in error handler:', handlingError);
      return false;
    }
  }

  /**
   * Handle API-specific errors
   */
  handleApiError(apiError: ApiError): void {
    try {
      const errorData = {
        ...apiError,
        timestamp: Date.now(),
        environment: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        }
      };

      this.logger.error('API Error', errorData);
    } catch (handlingError) {
      // eslint-disable-next-line no-console
      console.error('Error in API error handler:', handlingError);
    }
  }

  /**
   * Handle validation errors
   */
  handleValidationError(validationError: ValidationError): void {
    try {
      const errorData = {
        ...validationError,
        timestamp: Date.now(),
        environment: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        }
      };

      this.logger.warn('Validation Error', errorData);
    } catch (handlingError) {
      // eslint-disable-next-line no-console
      console.error('Error in validation error handler:', handlingError);
    }
  }

  logError(error: Error, context: ErrorContext): void {
    this.handleError(error, context);
  }

  getErrorLogs(): ErrorLog[] {
    return [...this.errorLogs];
  }

  clearErrorLogs(): void {
    this.errorLogs = [];
  }

  /**
   * Log operation start
   */
  logOperationStart(operation: string, data?: UnknownObject): void {
    try {
      const logData = {
        ...data,
        timestamp: Date.now()
      };

      this.logger.info(`Operation started: ${operation}`, logData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging operation start:', error);
    }
  }

  /**
   * Log operation success
   */
  logOperationSuccess(operation: string, data?: UnknownObject): void {
    try {
      const logData = {
        ...data,
        timestamp: Date.now()
      };

      this.logger.info(`Operation completed successfully: ${operation}`, logData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging operation success:', error);
    }
  }

  /**
   * Log operation failure
   */
  logOperationFailure(operation: string, error: Error): void {
    try {
      const logData = {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name
        },
        timestamp: Date.now()
      };

      this.logger.error(`Operation failed: ${operation}`, logData);
    } catch (handlingError) {
      // eslint-disable-next-line no-console
      console.error('Error logging operation failure:', handlingError);
    }
  }

  /**
   * Log API requests
   */
  logApiRequest(requestData: ApiRequestData): void {
    try {
      const logData = {
        ...requestData,
        timestamp: Date.now()
      };

      this.logger.debug(`API Request: ${requestData.method} ${requestData.url}`, logData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging API request:', error);
    }
  }

  /**
   * Log API responses
   */
  logApiResponse(responseData: ApiResponseData): void {
    try {
      const logData = {
        ...responseData,
        timestamp: Date.now()
      };

      this.logger.debug(`API Response: ${responseData.status} ${responseData.url}`, logData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging API response:', error);
    }
  }

  /**
   * Log user actions
   */
  logUserAction(actionData: UserActionData): void {
    try {
      const logData = {
        ...actionData,
        timestamp: Date.now()
      };

      this.logger.info(`User action: ${actionData.action}`, logData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging user action:', error);
    }
  }

  /**
   * Log performance metrics
   */
  logPerformance(performanceData: PerformanceData): void {
    try {
      const logData = {
        ...performanceData,
        timestamp: Date.now()
      };

      this.logger.info(`Performance: ${performanceData.operation} took ${performanceData.duration}ms`, logData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging performance:', error);
    }
  }

  /**
   * Create context-aware error handler
   */
  createWithContext(context: ErrorContext): CentralizedErrorHandler {
    const contextualHandler = new CentralizedErrorHandler();
    
    // Override methods to include context
    const originalHandleError = contextualHandler.handleError.bind(contextualHandler);
    contextualHandler.handleError = (error: Error, additionalContext?: ErrorContext) => {
      return originalHandleError(error, { ...context, ...additionalContext });
    };

    return contextualHandler;
  }

  /**
   * Логирование предупреждений
   */
  logWarning(message: string, context: ErrorContext, data?: unknown): void {
    const contextLogger = logger.createContextLogger(`${context.component}.${context.operation}`);
    contextLogger.warn(message, {
      context,
      data,
      timestamp: new Date().toISOString()
    });
  }

  formatError(error: unknown): Error {
    // Native Error
    if (error instanceof Error) {
      return error;
    }

    // String errors
    if (typeof error === 'string') {
      return new Error(error);
    }

    // Nullish
    if (error == null) {
      return new Error('Неизвестная ошибка');
    }

    // Generic object
    if (typeof error === 'object') {
      const obj = error as Record<string, unknown>;

      // Direct message field
      if (typeof obj.message === 'string' && obj.message.trim()) {
        return new Error(obj.message);
      }

      // Supabase/PostgREST error shape: { code, message, details, hint }
      if ((typeof obj.code === 'string' || typeof obj.code === 'number') && typeof obj.details === 'string') {
        const msg = typeof obj.details === 'string'
          ? obj.details
          : (typeof obj.message === 'string' ? obj.message : 'Ошибка API');
        return new Error(msg);
      }

      // Nested error: { error: { message, details } }
      if (obj.error && typeof obj.error === 'object') {
        const nested = obj.error as Record<string, unknown>;
        const details = typeof nested.details === 'string' ? nested.details : undefined;
        const message = typeof nested.message === 'string' ? nested.message : undefined;
        const msg = details || message;
        if (msg) return new Error(msg);
      }

      // Data wrapper: { data: { message } }
      if (obj.data && typeof obj.data === 'object') {
        const d = obj.data as Record<string, unknown>;
        if (typeof d.message === 'string') {
          return new Error(d.message);
        }
      }

      // Fetch Response-like minimal info
      if (typeof obj.status === 'number' && typeof obj.statusText === 'string') {
        return new Error(`HTTP ${obj.status}: ${obj.statusText}`);
      }

      // Fallback: JSON stringify short
      try {
        const json = JSON.stringify(obj);
        if (json && json !== '{}') return new Error(json);
      } catch { /* ignore JSON stringify errors */ }
    }

    // Final fallback
    return new Error('Неизвестная ошибка');
  }
}

// Export the singleton instance
export const centralizedErrorHandler = CentralizedErrorHandler.getInstance();

export const useErrorHandler = (): {
  handleError: (error: unknown, context: ErrorContext, showToast?: boolean) => Error;
  handleAsyncError: <T>(asyncOperation: () => Promise<T>, context: ErrorContext, showToast?: boolean) => Promise<T | null>;
  showSuccess: (message: string) => void;
  getErrorLogs: () => ErrorLog[];
  clearErrorLogs: () => void;
  logOperationStart: (operation: string, data?: UnknownObject) => void;
  logOperationSuccess: (operation: string, data?: UnknownObject) => void;
  logApiRequest: (requestData: ApiRequestData) => void;
  logApiResponse: (responseData: ApiResponseData) => void;
  logUserAction: (actionData: UserActionData) => void;
  logWarning: (message: string, context: ErrorContext, data?: unknown) => void;
} => {
  const { toast } = useToast();
  const errorHandler = CentralizedErrorHandler.getInstance();

  const handleError = useCallback((
    error: unknown,
    context: ErrorContext,
    showToast = true
  ) => {
    const formattedError = errorHandler.formatError(error);
    errorHandler.logError(formattedError, context);

    if (showToast) {
      toast({
        title: "Произошла ошибка",
        description: formattedError.message,
        variant: "destructive",
      });
    }

    return formattedError;
  }, [toast, errorHandler]);

  const handleAsyncError = useCallback(async <T>(
    asyncOperation: () => Promise<T>,
    context: ErrorContext,
    showToast = true
  ): Promise<T | null> => {
    try {
      return await asyncOperation();
    } catch (error) {
      handleError(error, context, showToast);
      return null;
    }
  }, [handleError]);

  const showSuccess = useCallback((message: string) => {
    toast({
      title: "Успешно",
      description: message,
      variant: "default",
    });
  }, [toast]);

  return {
    handleError,
    handleAsyncError,
    showSuccess,
    getErrorLogs: errorHandler.getErrorLogs.bind(errorHandler),
    clearErrorLogs: errorHandler.clearErrorLogs.bind(errorHandler),
    logOperationStart: errorHandler.logOperationStart.bind(errorHandler),
    logOperationSuccess: errorHandler.logOperationSuccess.bind(errorHandler),
    logApiRequest: errorHandler.logApiRequest.bind(errorHandler),
    logApiResponse: errorHandler.logApiResponse.bind(errorHandler),
    logUserAction: errorHandler.logUserAction.bind(errorHandler),
    logWarning: errorHandler.logWarning.bind(errorHandler)
  };
};
